import { Loader, WindowManager } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { FC, ReactElement } from "react";
import { Fragment, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useServiceFactory } from "../../app/appServices";
import type { Form } from "../../components/form/form";
import { Status } from "../../components/status/status";
import type { PaymentPageForm } from "../../types/paymentPage";
import { AboutSection } from "./components/aboutSection/aboutSection";
import { AmountSection } from "./components/amountSection/amountSection";
import { BusinessDetailsSection } from "./components/businessDetailsSection/businessDetailsSection";
import { BusinessLocationSection } from "./components/businessLocationSection/businessLocationSection";
import { CompanyLogoSection } from "./components/companyLogoSection/companyLogoSection";
import { ContactDetailsSection } from "./components/contactDetailsSection/contactDetailsSection";
import { HeaderSection } from "./components/headerSection/headerSection";
import { OpeningHoursSection } from "./components/openingHoursSection/openingHoursSection";
import { SocialMedialSection } from "./components/socialMediaSection/socialMediaSection";
import { SubmitSection } from "./components/submitSection/submitSection";
import type { ObPaymentPageFormStore } from "./obPaymentPageFormStore";
import styles from "./obPaymentPage.module.scss";

export type FormSectionType = {
  form: Form<PaymentPageForm>;
  store: ObPaymentPageFormStore;
};

type SectionEntry = {
  id: string;
  filled: boolean;
  render: (props: FormSectionType) => ReactElement | null;
};

export const ObPaymentPageForm: FC = observer(() => {
  const store = useServiceFactory((x) => x.createObPaymentPageFormStore());
  const params = useParams();
  const [sections, setSections] = useState<SectionEntry[]>([]);
  const location = useLocation();

  useEffect(() => {
    store.setToken(new URLSearchParams(location.search).get("token"));
    void store.load(params.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load the page once on mount
  }, []);

  useEffect(() => {
    if (store.state !== "error" && store.state !== "loading") {
      const {
        logo,
        address,
        googleMapsPlaceId,
        paymentPageDescription,
        instagramLink,
        facebookLink,
        linkedinLink,
        workingHours,
        contactEmail,
        contactWebsite,
        contactPhone,
      } = store.state.form.values;

      const { modalHeaderForm } = store.state;
      const initialSections: SectionEntry[] = [
        {
          render: (props) => <CompanyLogoSection {...props} modalForm={modalHeaderForm} />,
          filled: !!logo,
          id: "CompanyLogoSection",
        },
        {
          render: (props) => <BusinessLocationSection {...props} />,
          filled: !!address && !!googleMapsPlaceId,
          id: "BusinessLocationSection",
        },
        {
          render: (props) => <AboutSection {...props} />,
          filled: !!paymentPageDescription,
          id: "AboutSection",
        },
        {
          render: (props) => <ContactDetailsSection {...props} />,
          filled: !!contactEmail || !!contactWebsite || !!contactPhone,
          id: "ContactDetailsSection",
        },
        {
          render: (props) => <OpeningHoursSection {...props} />,
          filled: !!workingHours?.length,
          id: "OpeningHoursSection",
        },
        {
          render: (props) => <SocialMedialSection {...props} />,
          filled: !!instagramLink || !!facebookLink || !!linkedinLink,
          id: "SocialMedialSection",
        },
      ];

      if (store.firstUse) {
        setSections(initialSections);
      } else {
        setSections(initialSections.sort((a, b) => Number(b.filled) - Number(a.filled)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild sections only when the store state changes
  }, [store.state]);

  if (store.state === "loading") {
    return <Loader />;
  }

  if (store.state === "error") {
    return <Status type="error" title="Something goes wrong..." canReload />;
  }
  const { form } = store.state;
  return (
    <div className={styles.wrapper}>
      <WindowManager>
        <HeaderSection form={form} store={store} modalForm={store.state.modalHeaderForm} />
        {form.getValue("beneficiaryName") && (
          <div
            className={classNames(
              styles.section,
              styles.withSeparator,
              form.getValue("logo") && !store.getValidationError("imageSize") && styles.withMargin,
            )}
          >
            <span className={styles.beneficiaryName}>{form.getValue("beneficiaryName")}</span>
            <span className={styles.businessType}>{form.getValue("businessType")}</span>
          </div>
        )}
        <div className={styles.fieldsSection}>
          <AmountSection form={form} store={store} />
          {!store.firstUse && <BusinessDetailsSection form={form} />}
          {sections.map(({ id, render }) => (
            <Fragment key={id}>{render({ form, store })}</Fragment>
          ))}
          <SubmitSection form={form} store={store} />
        </div>
      </WindowManager>
    </div>
  );
});
